import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Input, InputGroup, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { fetchDataAgnostic, postDataAgnostic } from 'Services/dataApi';
import { DepartmentContext } from 'DepartmentWrapper';
import { formatBearerToken } from 'Common.functions';
import { toast } from 'react-toastify';
import style from './GenerateApplicationURLCode.style';
import { useAuth0 } from 'auth0/reactAuth0Spa';
import ApplicationForm from '../ApplicantProfile/ApplyPage/ApplicationForm/ApplicationForm';
import { faCopy, faEye, faRedoAlt } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

const GenerateApplicationURLCode = (props) => {
  const [showFormPreview, setShowFormPreview] = useState(false);
  const [isGeneratingNewCode, setIsGeneratingNewCode] = useState(false);
  const [departmentApplicantData, setDepartmentApplicantData] = useState([]);
  const [baseUrl, setBaseUrl] = useState('');

  const [isFetchingDepartmentApplicantData, setIsFetchingDepartmentApplicantData] = useState(false);

  const { getTokenSilently } = useAuth0();
  const dContext = useContext(DepartmentContext);
  // Alert functionality replaced with toast
  const { windowWidth, widthToSwitchToMobile } = props;

  useEffect(() => {
    fetchDepartmentApplicantData();
    const { protocol, hostname, port } = window.location;
    const newBaseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    setBaseUrl(newBaseUrl);
  }, []);

  const fetchDepartmentApplicantData = () => {
    setIsFetchingDepartmentApplicantData(true);
    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/application/list',
          { pk_Department: dContext.department.pk_Department },
          formatBearerToken(token),
        )
          .then((res) => {
            setDepartmentApplicantData(res.data);
          })
          .catch((err) => {
            console.log('fetchDepartmentApplicantData err: ', err);
          })
          .finally(() => {
            setIsFetchingDepartmentApplicantData(false);
          });
      })
      .catch((err) => {
        setIsFetchingDepartmentApplicantData(false);
        console.log('getToken err: ', err);
      });
  };

  const generateNewCode = () => {
    setIsGeneratingNewCode(true);
    getTokenSilently()
      .then((token) => {
        postDataAgnostic(
          'department/application/code',
          { pk_Department: dContext.department.pk_Department },
          null,
          formatBearerToken(token),
        )
          .then((result) => {
            console.log('app code result: ', result);
            setIsGeneratingNewCode(false);
            toast.success('New code generated successfully');
            fetchDepartmentApplicantData();
          })
          .catch((err) => {
            console.log('err: ', err);
            setIsGeneratingNewCode(false);
            toast.error('Failed to generate new code');
          });
      })
      .catch((err) => {
        setIsGeneratingNewCode(false);
        console.log('getToken err: ', err);
      });
  };

  const renderFormPreview = () => {
    return (
      <Modal
        isOpen={showFormPreview}
        toggle={() => setShowFormPreview(!showFormPreview)}
        size="lg"
        style={{ minWidth: '90vw' }}
      >
        <ModalHeader toggle={() => setShowFormPreview(!showFormPreview)}>Application Form Preview</ModalHeader>

        <ModalBody>
          <ApplicationForm isReadOnly={true} />
        </ModalBody>
      </Modal>
    );
  };

  const { Department = {} } = departmentApplicantData || {};

  const isMobile = windowWidth && widthToSwitchToMobile && windowWidth < widthToSwitchToMobile;
  return (
    <Card style={{ width: '100%' }}>
      <CardHeader
        style={
          isMobile ? { fontWeight: 'bold', display: 'flex', flexDirection: 'column', gap: 10 } : { fontWeight: 'bold' }
        }
      >
        <div
          style={
            isMobile
              ? { display: 'flex', flexDirection: 'column', gap: 10 }
              : { ...style.simpleRow, justifyContent: 'space-between' }
          }
        >
          <div style={{ ...style.simpleColumn, width: 'auto' }}>Application URL </div>
          <div style={{ ...style.simpleColumn, width: 'auto' }}>
            <div
              style={
                isMobile
                  ? { display: 'flex', flexDirection: 'column', gap: 10 }
                  : { ...style.simpleRow, justifyContent: 'space-between', alignItems: 'center' }
              }
            >
              <Button
                color="primary"
                size="sm"
                style={{ marginRight: isMobile ? 0 : 10, marginBottom: isMobile ? 10 : 0 }}
                onClick={() => {
                  setShowFormPreview(true);
                }}
              >
                <FontAwesomeIcon icon={faEye} /> Preview Application Form
              </Button>

              <Button
                color="success"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${baseUrl}/applicant/profile/new_application?applicationCode=${Department.ApplicantCode}`,
                  );
                  toast.success('URL copied to clipboard');
                }}
              >
                <FontAwesomeIcon icon={faCopy} /> Copy
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
            <Input
              type="text"
              bsSize="sm"
              disabled={true}
              value={`${baseUrl}/applicant/profile/new_application?applicationCode=${Department.ApplicantCode}`}
              style={{ width: '100%', maxWidth: 500, textAlign: 'center' }}
            />
            <Button
              disabled={isGeneratingNewCode}
              color="danger"
              size="sm"
              style={{ width: '100%', maxWidth: 300 }}
              onClick={() => {
                Swal.fire({
                  title: 'Are you sure?',
                  text:
                    'This will generate a new code and invalidate the current one. Are you sure you want to proceed?',
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonText: 'Yes',
                  cancelButtonText: 'No',
                }).then((result) => {
                  if (result.isConfirmed) {
                    generateNewCode();
                  }
                });
              }}
            >
              Generate New URL
              <FontAwesomeIcon icon={faRedoAlt} style={{ marginLeft: 10 }} />
            </Button>
          </div>
        ) : (
          <div>
            <InputGroup>
              <Button
                disabled={isGeneratingNewCode}
                color="danger"
                size="sm"
                onClick={() => {
                  Swal.fire({
                    title: 'Are you sure?',
                    text:
                      'This will generate a new code and invalidate the current one. Are you sure you want to proceed?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                  }).then((result) => {
                    if (result.isConfirmed) {
                      generateNewCode();
                    }
                  });
                }}
              >
                Generate New URL
                <FontAwesomeIcon icon={faRedoAlt} style={{ marginLeft: 10 }} />
              </Button>

              <Input
                type="text"
                bsSize="sm"
                disabled={true}
                value={`${baseUrl}/applicant/profile/new_application?applicationCode=${Department.ApplicantCode}`}
                style={{ width: 300 }}
              />
            </InputGroup>
          </div>
        )}
      </CardBody>
      {renderFormPreview()}
    </Card>
  );
};

export default GenerateApplicationURLCode;
